import { useTranslation } from 'react-i18next'

import {
  useDictationSupported,
  useVoiceDraft,
} from '#/shared/lib/voice-dictation'
import { cn } from '#/shared/lib/utils'
import { MicIcon } from '#/shared/ui/icons'
import { Tooltip } from '#/shared/ui/tooltip'

import { VoiceLoadingSpinner } from './voice-loading-spinner'
import { VoiceProcessingIsland } from './voice-processing-island'
import { VoiceRecordingIsland } from './voice-recording-island'

/**
 * Drop-in voice dictation control for a plain-text field: a mic button that
 * swaps to the animated recording island while listening, appending the smart-
 * formatted transcript onto `value` via {@link useVoiceDraft}. The owner just
 * wires the same controlled `value`/`onChange` it gives its textarea.
 *
 * `className` applies to both states — use it for positioning (e.g. anchoring
 * to a textarea corner). Don't pass width/height-fixing tokens through it: the
 * recording island needs its own pill shape, so `size-*` would collapse it into
 * a circle and clip the waveform.
 */
export function VoiceInputButton({
  value,
  onChange,
  disabled = false,
  className,
}: {
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const supported = useDictationSupported()
  const {
    recording,
    loading,
    processing,
    voiceStream,
    startRecording,
    stopRecording,
  } = useVoiceDraft(value, onChange)

  // A mobile browser without the native speech API has no dictation path —
  // no mic at all beats a mic that can't record.
  if (!supported) return null
  if (recording) {
    return (
      <VoiceRecordingIsland
        onStop={stopRecording}
        stream={voiceStream}
        className={cn('h-8', className)}
      />
    )
  }
  // Listening ended — transcribing the clip; show the waiting animation in place.
  if (processing) {
    return <VoiceProcessingIsland className={cn('h-8', className)} />
  }
  // Opening the mic / transcription stream — show the spinner in the button's
  // place until recording begins.
  if (loading) {
    return (
      <Tooltip content={t('voice.preparing')}>
        <div
          role="status"
          aria-label={t('voice.preparing')}
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full text-gray-500',
            className,
          )}
        >
          <VoiceLoadingSpinner />
        </div>
      </Tooltip>
    )
  }
  return (
    <Tooltip content={t('voice.record')}>
      <button
        type="button"
        onClick={startRecording}
        disabled={disabled}
        aria-label={t('voice.record')}
        className={cn(
          'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 transition-colors duration-200 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-500',
          className,
        )}
      >
        <MicIcon className="size-4.5" />
      </button>
    </Tooltip>
  )
}
